// store.jsx — Contexto local de CardRequirements (acciones + UI state)
// Requiere estar dentro de QuotationByIdMultiRoleProvider (padre)

import { createContext } from "preact";
import { useContext, useMemo, useState, useCallback } from "preact/hooks";
import { fetchToServer } from "@shared/components";
import { useQuotationByIdMultiRole } from "../store"; // ajusta la ruta real

const RequirementsCardContext = createContext(null);

export function useRequirementsCard() {
  const ctx = useContext(RequirementsCardContext);
  if (!ctx) throw new Error("useRequirementsCard must be used within RequirementsCardProvider");
  return ctx;
}

// Rutas del backend para requerimientos
function makeReqPath(role, quotationId, kind, reqId) {
  const base = `/${role}/api/quotations/${quotationId}/requirements`;
  if (kind === "create") return `${base}/create`;
  if (kind === "delete") return `${base}/${reqId}/delete`;
  if (kind === "status") return `${base}/${reqId}/accept`;
  throw new Error("Unknown URL kind");
}

export function RequirementsCardProvider({ children }) {
  const { role, quotationId, quotation, refresh } = useQuotationByIdMultiRole();

  const [openModal, setOpenModal] = useState("none"); // "none" | "create" | "delete"
  const [selectedReqId, setSelectedReqId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [changing, setChanging] = useState(false);
  const [error, setError] = useState(null);

  const requirements = useMemo(
    () => (Array.isArray(quotation?.requirements) ? quotation.requirements : []),
    [quotation]
  );

  const openCreateModal = useCallback(() => setOpenModal("create"), []);
  const openDeleteModal = useCallback((reqId) => { if (reqId) setSelectedReqId(reqId); setOpenModal("delete"); }, []);
  const closeModal = useCallback(() => setOpenModal("none"), []);

  const createRequirement = useCallback(
    async ({ description }) => {
      if (!quotationId) throw new Error("quotationId missing");
      setCreating(true);
      setError(null);
      try {
        await fetchToServer({
          url: makeReqPath(role, quotationId, "create"),
          method: "post",
          data: { description },
          withCredentials: true,
        });
        await refresh();
      } catch (e) {
        setError(e);
        throw e;
      } finally {
        setCreating(false);
      }
    },
    [role, quotationId, refresh]
  );

  const deleteRequirement = useCallback(
    async (reqId) => {
      const targetId = reqId || selectedReqId;
      if (!quotationId) throw new Error("quotationId missing");
      if (!targetId) throw new Error("requirementId missing");
      setDeleting(true);
      setError(null);
      try {
        await fetchToServer({
          url: makeReqPath(role, quotationId, "delete", targetId),
          method: "delete",
          withCredentials: true,
        });
        setSelectedReqId((prev) => (prev === targetId ? null : prev));
        await refresh();
      } catch (e) {
        setError(e);
        throw e;
      } finally {
        setDeleting(false);
      }
    },
    [role, quotationId, selectedReqId, refresh]
  );

  const changeRequirementStatus = useCallback(
    async (reqId, statusName) => {
      if (!quotationId) throw new Error("quotationId missing");
      if (!reqId) throw new Error("requirementId missing");
      setChanging(true);
      setError(null);
      try {
        await fetchToServer({
          url: makeReqPath(role, quotationId, "status", reqId),
          method: "get",
          params: { name: statusName },
          withCredentials: true,
        });
        await refresh();
      } catch (e) {
        setError(e);
        throw e;
      } finally {
        setChanging(false);
      }
    },
    [role, quotationId, refresh]
  );

  const value = useMemo(
    () => ({
      quotation,
      role,
      quotationId,
      requirements,
      // UI
      openModal,
      selectedReqId,
      creating,
      deleting,
      changing,
      error,
      // acciones UI
      setSelectedReqId,
      openCreateModal,
      openDeleteModal,
      closeModal,
      // acciones negocio
      createRequirement,
      deleteRequirement,
      changeRequirementStatus,
    }),
    [
      quotation,
      role,
      quotationId,
      requirements,
      openModal,
      selectedReqId,
      creating,
      deleting,
      changing,
      error,
      openCreateModal,
      openDeleteModal,
      closeModal,
      createRequirement,
      deleteRequirement,
      changeRequirementStatus,
    ]
  );

  return (
    <RequirementsCardContext.Provider value={value}>
      {children}
    </RequirementsCardContext.Provider>
  );
}
